/**
 * affiliate-withdrawals-page.tsx — Platform → Tiếp thị liên kết → Lệnh rút tiền. Route `/platform/affiliate/withdrawals`.
 *
 * Quy trình chi thủ công: Chờ duyệt → Duyệt (tính lại thuế TNCN) → Xuất file chuyển khoản (CSV, chọn lệnh hoặc tất cả
 * lệnh đã duyệt) → chuyển tiền ở ngân hàng → "Đã chi" (mã giao dịch, ngày) → đối tác nhận thông báo. Từ chối: tiền về số dư.
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { AlertTriangle, Download, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import { reexportConfirmText, taxRuleLabel, withdrawalStatusLabel, WITHDRAWAL_STATUS_TONE } from '@/lib/affiliate'
import {
  AlreadyExportedError, exportWithdrawals, useAdminWithdrawals, useApproveWithdrawal, useMarkWithdrawalPaid, useRejectWithdrawal, type AdminWithdrawal,
} from '@/hooks/use-platform-affiliate'
import { useQueryClient } from '@tanstack/react-query'
import { FilterChips, fmtDate, fmtDateTime, ReasonDialog } from './affiliate-shared'

export function AffiliateWithdrawalsPage() {
  const [sp, setSp] = useSearchParams()
  const status = sp.get('status') ?? 'pending'
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  const [rejecting, setRejecting] = useState<AdminWithdrawal | null>(null)
  const [paying, setPaying] = useState<AdminWithdrawal | null>(null)
  const [exporting, setExporting] = useState(false)
  const { data, isLoading } = useAdminWithdrawals(status, page)
  const approve = useApproveWithdrawal()
  const reject = useRejectWithdrawal()
  const qc = useQueryClient()

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const doExport = async () => {
    setExporting(true)
    try {
      let r
      try {
        r = await exportWithdrawals(selected)
      } catch (e) {
        // E17: lệnh đã xuất ở lô trước → hỏi xác nhận rồi mới xuất lại (force).
        if (!(e instanceof AlreadyExportedError)) throw e
        if (!window.confirm(reexportConfirmText(e.exported))) return
        r = await exportWithdrawals(selected.length ? selected : e.exported.map((x) => x.id), true)
      }
      toast.success(`Đã xuất file lô ${r.batch}${r.reExported ? ` (xuất lại ${r.reExported} lệnh)` : ''}`)
      if (r.skippedSuspended) toast.warning(`Bỏ qua ${r.skippedSuspended} lệnh của đối tác đang bị khoá`)
      setSelected([])
      qc.invalidateQueries({ queryKey: ['platform', 'affiliate'] })
    } catch (e) {
      toast.error(e instanceof Error && !('response' in e) ? e.message : platformApiError(e))
    } finally {
      setExporting(false)
    }
  }

  const columns: Column<AdminWithdrawal>[] = [
    ...(status === 'approved' ? [{
      key: 'sel', header: '', className: 'w-8',
      cell: (w: AdminWithdrawal) => <Checkbox checked={selected.includes(w.id)} onCheckedChange={() => toggle(w.id)} aria-label="Chọn" />,
    }] : []),
    {
      key: 'code', header: 'Lệnh',
      cell: (w) => (
        <div>
          <p className="font-mono text-xs">{w.code}</p>
          <p className="text-xs text-muted-foreground">{fmtDateTime(w.requestedAt)}</p>
        </div>
      ),
    },
    {
      key: 'partner', header: 'Đối tác',
      cell: (w) => (
        <div>
          <p>{w.partnerName} <span className="font-mono text-xs text-muted-foreground">({w.partnerCode})</span></p>
          <p className="text-xs text-muted-foreground">{w.payoutMasked}</p>
          {w.payoutChangedRecently && (
            <p className="flex items-center gap-1 text-xs text-warning"><AlertTriangle className="h-3 w-3" /> Vừa đổi thông tin nhận tiền — kiểm tra kỹ</p>
          )}
          {w.partnerStatus !== 'active' && (w.status === 'pending' || w.status === 'approved') && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <AlertTriangle className="h-3 w-3" /> Đối tác đang bị khoá — không duyệt / chi / xuất file được tới khi mở khoá (vẫn từ chối được)
            </p>
          )}
          {w.partnerStatus !== 'active' && w.status !== 'pending' && w.status !== 'approved' && <p className="text-xs text-destructive">Đối tác đang bị khoá</p>}
        </div>
      ),
    },
    { key: 'amount', header: 'Số tiền', align: 'right', cell: (w) => formatVnd(w.amount) },
    {
      key: 'tax', header: 'Thuế TNCN', align: 'right',
      cell: (w) => (
        <div>
          <p>{formatVnd(w.taxWithheld)}</p>
          <p className="max-w-[180px] text-[11px] text-muted-foreground">{taxRuleLabel(w.taxRule) ?? w.taxRule}</p>
        </div>
      ),
    },
    { key: 'net', header: 'Thực chi', align: 'right', cell: (w) => <span className="font-medium">{formatVnd(w.netAmount)}</span> },
    {
      key: 'status', header: 'Trạng thái',
      cell: (w) => (
        <div>
          <Badge variant={WITHDRAWAL_STATUS_TONE[w.status]}>{withdrawalStatusLabel(w.status)}</Badge>
          {w.exportedAt && w.status === 'approved' && <p className="text-[11px] text-muted-foreground">Đã xuất {fmtDate(w.exportedAt)} · {w.exportBatch}</p>}
          {w.status === 'paid' && <p className="text-[11px] text-muted-foreground">{fmtDate(w.paidAt)} · GD {w.transferRef}</p>}
          {w.status === 'rejected' && <p className="max-w-[200px] truncate text-[11px] text-muted-foreground" title={w.rejectReason ?? ''}>{w.rejectReason}</p>}
        </div>
      ),
    },
    {
      key: 'act', header: '', align: 'right',
      cell: (w) => (
        <div className="flex justify-end gap-1">
          {w.status === 'pending' && (
            <Button size="sm" disabled={approve.isPending || w.partnerStatus !== 'active'} onClick={() => {
              if (!window.confirm(`Duyệt lệnh ${w.code} (${formatVnd(w.amount)})? Thuế TNCN được tính lại theo quy tắc đang áp.`)) return
              approve.mutate(w.id, { onSuccess: () => toast.success('Đã duyệt'), onError: (e) => toast.error(platformApiError(e)) })
            }}>Duyệt</Button>
          )}
          {w.status === 'approved' && <Button size="sm" disabled={w.partnerStatus !== 'active'} onClick={() => setPaying(w)}>Đã chi</Button>}
          {(w.status === 'pending' || w.status === 'approved') && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setRejecting(w)}>Từ chối</Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <PageHeader
        title="Lệnh rút tiền"
        description="Duyệt lệnh, xuất file chuyển khoản, chuyển tiền thủ công rồi đánh dấu đã chi. Quy tắc thuế TNCN phải được kế toán xác nhận theo luật hiện hành."
        actions={status === 'approved' && (
          <Button onClick={doExport} disabled={exporting || !data?.items.length}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Xuất file chuyển khoản {selected.length ? `(${selected.length} lệnh)` : '(lệnh đã duyệt chưa xuất)'}
          </Button>
        )}
      />
      <FilterChips
        value={status}
        onChange={(v) => { setSp(v ? { status: v } : {}, { replace: true }); setPage(1); setSelected([]) }}
        options={[['pending', 'Chờ duyệt'], ['approved', 'Đã duyệt — chờ chuyển'], ['paid', 'Đã chi'], ['rejected', 'Từ chối'], ['cancelled', 'Đối tác huỷ'], ['', 'Tất cả']]}
      />
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(w) => w.id} emptyTitle="Không có lệnh" />
      {data && data.total > data.limit && <Pagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />}
      <p className="text-xs text-muted-foreground">
        File CSV (mở bằng Excel) gồm: người nhận, CCCD/MST, ngân hàng, số tài khoản đầy đủ, số tiền gộp, thuế TNCN khấu trừ, thực chi, nội dung
        chuyển khoản. File chứa dữ liệu nhạy cảm — lưu trữ an toàn. Chưa hỗ trợ XLSX (repo chưa có thư viện) và ví chi hộ tự động.
      </p>
      <ReasonDialog
        open={!!rejecting}
        title="Từ chối lệnh rút"
        description={rejecting ? `Lệnh ${rejecting.code} — số tiền trở lại số dư của đối tác.` : undefined}
        confirmLabel="Từ chối"
        destructive
        pending={reject.isPending}
        onClose={() => setRejecting(null)}
        onConfirm={(reason) => rejecting && reject.mutate({ id: rejecting.id, reason }, {
          onSuccess: () => { toast.success('Đã từ chối'); setRejecting(null) },
          onError: (e) => toast.error(platformApiError(e)),
        })}
      />
      <PaidDialog w={paying} onClose={() => setPaying(null)} />
    </div>
  )
}

function PaidDialog({ w, onClose }: { w: AdminWithdrawal | null; onClose: () => void }) {
  const paid = useMarkWithdrawalPaid()
  const [ref, setRef] = useState('')
  const [date, setDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  return (
    <Dialog open={!!w} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Đánh dấu đã chi</DialogTitle>
          <DialogDescription>{w ? `Lệnh ${w.code}: đã chuyển ${formatVnd(w.netAmount)} cho ${w.partnerName}.` : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="paid-ref">Mã giao dịch ngân hàng</Label>
            <Input id="paid-ref" value={ref} onChange={(e) => setRef(e.target.value)} maxLength={100} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="paid-date">Ngày chuyển</Label>
            <Input id="paid-date" type="date" value={date} max={dayjs().format('YYYY-MM-DD')} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
          <Button
            disabled={ref.trim().length < 3 || paid.isPending}
            onClick={() => w && paid.mutate({ id: w.id, transferRef: ref.trim(), paidAt: date === dayjs().format('YYYY-MM-DD') ? new Date().toISOString() : dayjs(date).hour(12).toISOString() }, {
              onSuccess: () => { toast.success('Đã ghi nhận chi — đối tác được thông báo'); setRef(''); onClose() },
              onError: (e) => toast.error(platformApiError(e)),
            })}
          >
            {paid.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Xác nhận đã chi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
